import React from "react";
import { History } from "lucide-react";
import GlobalDetailDrawer from "modules/swiftboard/components/GlobalDetailDrawer";
import LocalDateTime from "services/date-converter";
import Chip from "../../../components/Chip";
import { FILTERED_OUT_LABEL } from "../../../features/builder.constants";
import MessageList from "../../../components/MessageList";
import StatusPill from "../../components/StatusPill";
import { RUN_SOURCE_LABEL, RUN_STATUS } from "../../features/monitor.constants";
import { durationText } from "../../features/monitor.utils";

/** What a test run would have done (test_result): counts and the first cases with who step 1 reaches. */
function TestResult({ result }) {
  const filtered = Object.entries(result.filtered_out ?? {}).filter(([, n]) => n > 0);
  return (
    <div className="space-y-2 text-xs">
      <div className="flex flex-wrap gap-1">
        <Chip>{result.candidates} found</Chip>
        <Chip tone="brand">{result.would_open} would open</Chip>
        {filtered.map(([reason, n]) => (
          <Chip key={reason}>{`${FILTERED_OUT_LABEL[reason] ?? reason}: ${n}`}</Chip>
        ))}
      </div>
      {result.cases?.length > 0 && (
        <ul className="divide-y divide-border_soft rounded-lg border border-border">
          {result.cases.map((c) => (
            <li key={`${c.subject_key}-${c.reference_key}`} className="px-3 py-1.5">
              <p className="font-medium text-text_primary">{c.user?.name ?? c.subject_key}</p>
              <p className="truncate text-[11px] text-text_muted">
                Ref {c.reference_key} · to {(c.recipients ?? []).map((p) => p.name || p.email).join(", ") || "nobody"}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Read-only view of one rule evaluation (fetched by the list page).
export default function RunLogViewDrawer({ record, onClose }) {
  const isTest = record.source === "TEST";
  const sections = [
    ...(record.error_message ? [{ title: record.status === "FAILED" ? "Error" : "Note", fields: [{ value: <MessageList tone={record.status === "FAILED" ? "problem" : "warning"} items={[record.error_message]} /> }] }] : []),
    {
      title: "Run",
      fields: [
        { label: "Rule", value: record.rule ? `${record.rule.name} (${record.rule.code})` : `#${record.rule_fid}` },
        { label: "Started by", value: RUN_SOURCE_LABEL[record.source] ?? record.source },
        { label: "Status", value: <StatusPill status={record.status} map={RUN_STATUS} /> },
        { label: "Rule version", value: record.published_version_no ? `v${record.published_version_no}` : "Draft" },
        { label: "Slot", value: <LocalDateTime utcTime={record.run_slot} /> },
        { label: "Started", value: <LocalDateTime utcTime={record.started_at} /> },
        { label: "Finished", value: record.finished_at ? <LocalDateTime utcTime={record.finished_at} /> : "Still running" },
        { label: "Took", value: durationText(record.duration_ms) },
        ...(record.query_duration_ms !== null && record.query_duration_ms !== undefined ? [{ label: "Reading records", value: durationText(record.query_duration_ms) }] : []),
      ],
    },
    isTest && record.test_result
      ? { title: "What it would do", fields: [{ value: <TestResult result={record.test_result} /> }] }
      : {
          title: "Result",
          fields: [
            { label: "Found by the trigger", value: record.candidates },
            { label: "Left out by the audience", value: record.filtered_out },
            { label: "Cases opened", value: record.opened },
            { label: "Already open, seen again", value: record.updated },
            { label: "Resolved", value: record.resolved },
            { label: "Cases sent", value: record.dispatch_enqueued },
            { label: "Pages read", value: record.pages },
          ],
        },
  ];

  return <GlobalDetailDrawer open onClose={onClose} title={record.rule?.name ?? "Reminder run"} subtitle={`Run #${record.id}`} icon={<History size={20} />} sections={sections} />;
}
